import { useHead } from '@unhead/react'

export default function SEOHead() {
  useHead({
    title: 'Smart Sleep Calculator | Free Online Sleep Cycle & Bedtime Calculator 2024',
    meta: [
      {
        name: 'description',
        content:
          'Free online sleep calculator — calculate wake up time, bedtime, sleep cycles using 90-minute REM science. Includes STOP-BANG, Epworth, CPAP/AHI, nap planner, jet lag & sleep debt tools.',
      },
      {
        name: 'keywords',
        content:
          'online sleep calculator, sleepyti me bedtime calculator, wake up calculator, stop bang calculator, calculate wake up time, calculate sleep efficiency, 90 minute sleep cycle calculator, jetlag calculator, rem cycle timer, sleep length calculator, rem sleep cycle timer, sleep time calc, sleep clock calculator, sleep duration calculator, melatonin jet lag calculator, stop bang score calculator, 90 min sleep calculator, cpap pressure calculator, sleep now calculator, sleep cycle calculator age, sleep wake calculator, wakeup calculator, 8 hour sleep calculator, bedtime calculator by age, nap time calculator, sleep amount calculator, sleep wake up calculator, if i sleep at 10pm when should i wake up, circadian sleep calculator, deep sleep calculator, power nap calculator, sleep calculator nap, perfect sleep calculator, sleep rhythm calculator',
      },
      { name: 'robots', content: 'index, follow' },
      { name: 'author', content: 'Smart Sleep Calculator' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { property: 'og:type', content: 'website' },
      {
        property: 'og:title',
        content: 'Smart Sleep Calculator | Complete Sleep Health Diagnostics Suite',
      },
      {
        property: 'og:description',
        content:
          'Calculate your perfect wake-up time, bedtime, nap schedule, and screen for sleep disorders with our free, science-backed sleep tools.',
      },
      { property: 'og:url', content: 'https://www.smartsleepcalculator.com' },
      { property: 'og:site_name', content: 'Smart Sleep Calculator' },
      { name: 'twitter:card', content: 'summary_large_image' },
      {
        name: 'twitter:title',
        content: 'Smart Sleep Calculator — Free Online Sleep Cycle Tool',
      },
      {
        name: 'twitter:description',
        content:
          'Free sleep cycle calculator. Get personalized wake-up times, nap schedules, STOP-BANG screening, jet lag help & more.',
      },
      { name: 'theme-color', content: '#080c14' },
    ],
    link: [
      { rel: 'canonical', href: 'https://www.smartsleepcalculator.com' },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'Smart Sleep Calculator',
          url: 'https://www.smartsleepcalculator.com',
          applicationCategory: 'HealthApplication',
          description:
            'A comprehensive sleep health diagnostics suite featuring sleep cycle calculator, bedtime calculator, wake up time calculator, STOP-BANG sleep apnea screener, Epworth Sleepiness Scale, CPAP/AHI severity calculator, nap planner, jet lag calculator, and sleep debt tracker.',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          featureList: [
            'Sleep Cycle Calculator',
            'Wake Up Time Calculator',
            'Bedtime Calculator',
            'STOP-BANG Sleep Apnea Screener',
            'Epworth Sleepiness Scale',
            'CPAP AHI Calculator',
            'Power Nap Calculator',
            'Jet Lag Calculator',
            'Sleep Debt Tracker',
          ],
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'If I sleep at 10pm when should I wake up?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'If you sleep at 10pm (adding 15 min sleep latency), ideal wake times using 90-min REM cycles are: 11:45pm (1 cycle), 1:15am (2 cycles), 2:45am (3 cycles), 4:15am (4 cycles), 5:45am (5 cycles — recommended), or 7:15am (6 cycles — optimal for full rest).',
              },
            },
            {
              '@type': 'Question',
              name: 'How do I calculate my wake up time?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Enter your bedtime into our sleep calculator. It adds your sleep latency (time to fall asleep, typically 15 min), then calculates wake times at 1.5-hour REM cycle intervals. Choose a time that completes full cycles for the most refreshed awakening.',
              },
            },
            {
              '@type': 'Question',
              name: 'What is the STOP-BANG calculator?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The STOP-BANG questionnaire screens for obstructive sleep apnea risk. It asks 8 yes/no questions about Snoring, Tiredness, Observed apnea, blood Pressure, BMI, Age, Neck circumference, and Gender. Score 0–2 = Low risk, 3–4 = Intermediate risk, 5–8 = High risk.',
              },
            },
            {
              '@type': 'Question',
              name: 'What is a 90-minute sleep cycle calculator?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Human sleep naturally cycles through NREM and REM stages every ~90 minutes. A 90-minute sleep cycle calculator uses this science to recommend optimal bedtimes or wake times that align with natural cycle completions, so you wake refreshed instead of groggy mid-cycle.',
              },
            },
            {
              '@type': 'Question',
              name: 'How do I calculate sleep efficiency?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Sleep efficiency = (Total Sleep Time ÷ Time in Bed) × 100%. A healthy efficiency is 85% or higher. Our sleep efficiency calculator lets you enter your time in bed and actual sleep time to compute your score and get personalized improvement advice.',
              },
            },
          ],
        }),
      },
    ],
  })

  return null
}
